import { Sparkles, Check } from "lucide-react";
import { CandyButton } from "../playful/CandyButton";
import { GeometricBadge, MonoTag } from "../playful/GeometricBadge";
import { Blob, SectionPattern } from "../playful/Decor";
import { Reveal, useInView, reveal } from "./Reveal";
import { DashboardMock, PhoneBioMock } from "./mocks";
import LiveShortener from "./LiveShortener";
import { HERO } from "../../copy/landing";

const TRUST = HERO.trust;

/**
 * LandingHero — platform repositioning.
 * Headline + composed product collage (dashboard + bio phone + analytics),
 * with the working live shortener as a "try it" band beneath.
 */
export default function LandingHero() {
  const [ref, visible] = useInView(0.04);

  return (
    <section className="relative overflow-hidden border-b-2 border-ink" data-testid="hero-section">
      <SectionPattern kind="soft" opacity={0.5} />
      <Blob
        tone="candy"
        size={460}
        className="pointer-events-none absolute -right-40 -top-40 animate-drift opacity-40"
      />

      <div ref={ref} className="relative mx-auto max-w-[1400px] px-5 pb-16 pt-14 md:px-10 md:pt-20">
        {/* Badge row */}
        <div {...reveal(visible, 0)} className="flex flex-wrap items-center gap-3">
          <GeometricBadge tone="yellow" size="sm">
            <Sparkles size={11} strokeWidth={3} /> {HERO.badge}
          </GeometricBadge>
          <MonoTag>{HERO.kicker}</MonoTag>
        </div>

        {/* Headline */}
        <div className="mt-8 flex flex-col items-start gap-8 md:mt-10 lg:flex-row">
          <div className="relative z-30 w-full min-w-0 lg:w-1/2" {...reveal(visible, 100)}>
            <h1 className="font-display font-extrabold leading-[0.92] tracking-tight">
              <span className="block text-[12vw] sm:text-[9vw] lg:text-[5.6vw]">
                {HERO.headline[0]}
              </span>
              <span className="ai-gradient-text block text-[12vw] sm:text-[9vw] lg:text-[5.6vw]">
                {HERO.headline[1]}
              </span>
              {HERO.headline[2] ? (
                <span className="block text-[12vw] sm:text-[9vw] lg:text-[5.6vw]">
                  {HERO.headline[2]}
                </span>
              ) : null}
            </h1>
            <p className="mt-7 max-w-prose font-sans text-lg leading-relaxed text-ink/70 md:text-xl">
              {HERO.subtitleLead}
              <span className="font-semibold text-ink">{HERO.subtitleEmphasis}</span>
              {HERO.subtitleTail}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3" {...reveal(visible, 250)}>
              <CandyButton
                to="/signup"
                variant="candy"
                size="lg"
                withArrow
                data-testid="hero-cta-signup"
              >
                {HERO.ctaPrimary}
              </CandyButton>
              <CandyButton to="/pricing" variant="paper" size="lg" data-testid="hero-cta-pricing">
                {HERO.ctaSecondary}
              </CandyButton>
            </div>
            {/* Product Hunt badge */}
            <div className="mt-6" {...reveal(visible, 280)}>
              <a
                href="https://www.producthunt.com/products/linklane?utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-linklane"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  alt="LinkLane - Short links that tell you what happened after the click. | Product Hunt"
                  width="250"
                  height="54"
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1167684&theme=light&t=1781021062193"
                />
              </a>
            </div>

            {/* Trust microcopy */}
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2" {...reveal(visible, 320)}>
              {TRUST.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-ink/55"
                >
                  <Check size={13} strokeWidth={3} className="text-mint" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Composed product collage — dashboard anchor + one bio-page accent */}
          <div
            className="relative hidden w-full min-w-0 lg:block lg:w-1/2"
            {...reveal(visible, 200)}
          >
            <div className="relative animate-float" style={{ animationDuration: "5s" }}>
              <DashboardMock animate={visible} />
            </div>
            <div
              className="absolute -bottom-8 -left-4 z-20 animate-float"
              style={{ animationDuration: "6s", animationDelay: "0.6s" }}
            >
              <PhoneBioMock />
            </div>
          </div>
        </div>

        {/* Mobile / tablet single product cue */}
        <div className="mt-8 flex justify-center lg:hidden" {...reveal(visible, 300)}>
          <DashboardMock animate={visible} />
        </div>

        {/* Live shortener band */}
        <div className="mt-16 lg:mt-24" {...reveal(visible, 350)}>
          <LiveShortener />
        </div>
      </div>
    </section>
  );
}
